import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QrCode, Package, ArrowRightLeft, AlertTriangle, ClipboardList, Truck, Factory } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function MobileWarehousePage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/warehouse/dashboard`);
      setDashboard(res.data);
    } catch (err) {
      console.error('Warehouse dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="mobile-page">
        <div className="mobile-loading"><div className="mobile-spinner" /></div>
      </div>
    );
  }

  const utilization = dashboard?.overall_utilization || 0;
  const totalPallets = dashboard?.total_pallets || 0;
  const totalCapacity = dashboard?.total_capacity || 0;
  const holdsCount = dashboard?.pallets_on_hold || 0;
  const trucksAtDock = dashboard?.trucks_at_dock || 0;

  return (
    <div className="mobile-page">
      <div className="mobile-page-header">
        <div>
          <div className="mobile-page-title">Warehouse</div>
          <div className="mobile-page-subtitle">Operations hub</div>
        </div>
      </div>

      {/* KPIs */}
      <div className="mobile-kpi-row">
        <div className="mobile-kpi-card blue">
          <div className="mobile-kpi-label">Utilization</div>
          <div className="mobile-kpi-value">{utilization.toFixed(1)}%</div>
          <div className="mobile-kpi-detail">{totalPallets} / {totalCapacity}</div>
        </div>
        <div className="mobile-kpi-card red">
          <div className="mobile-kpi-label">On Hold</div>
          <div className="mobile-kpi-value">{holdsCount}</div>
          <div className="mobile-kpi-detail">pallets</div>
        </div>
        <div className="mobile-kpi-card green">
          <div className="mobile-kpi-label">Trucks</div>
          <div className="mobile-kpi-value">{trucksAtDock}</div>
          <div className="mobile-kpi-detail">at dock</div>
        </div>
        <div className="mobile-kpi-card purple">
          <div className="mobile-kpi-label">Zones</div>
          <div className="mobile-kpi-value">{dashboard?.zones?.length || 0}</div>
          <div className="mobile-kpi-detail">active</div>
        </div>
      </div>

      {/* Zone Utilization */}
      {dashboard?.zones && dashboard.zones.length > 0 && (
        <div className="mobile-detail-section" style={{ marginBottom: 16 }}>
          <div className="mobile-detail-section-title">Zone Utilization</div>
          {dashboard.zones.map((zone, i) => {
            const pct = zone.capacity > 0 ? (zone.occupied / zone.capacity) * 100 : 0;
            return (
              <div key={i} style={{ marginBottom: i < dashboard.zones.length - 1 ? 10 : 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span style={{ fontWeight: 500, color: '#1c1c1e' }}>{zone.zone_name}</span>
                  <span style={{ color: '#8e8e93' }}>{zone.occupied}/{zone.capacity} ({pct.toFixed(1)}%)</span>
                </div>
                <div style={{ height: 6, background: '#f2f2f7', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: pct > 80 ? '#ff3b30' : pct > 50 ? '#ff9500' : '#34c759', borderRadius: 3, transition: 'width 0.3s' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Operations */}
      <div className="mobile-section-header">
        <span className="mobile-section-title">Operations</span>
      </div>

      <div className="mobile-hub-grid">
        <button className="mobile-hub-card" onClick={() => navigate('/mobile/warehouse/work-orders')}>
          <div className="mobile-hub-card-icon" style={{ background: '#1a1a2e' }}>
            <Factory size={24} />
          </div>
          <div className="mobile-hub-card-title">Work Orders</div>
          <div className="mobile-hub-card-subtitle">Pick & return inventory</div>
        </button>

        <button className="mobile-hub-card" onClick={() => navigate('/mobile/warehouse/scan')}>
          <div className="mobile-hub-card-icon" style={{ background: '#007aff' }}>
            <QrCode size={24} />
          </div>
          <div className="mobile-hub-card-title">Scan LP</div>
          <div className="mobile-hub-card-subtitle">Look up license plate</div>
        </button>

        <button className="mobile-hub-card" onClick={() => navigate('/mobile/warehouse/receive')}>
          <div className="mobile-hub-card-icon" style={{ background: '#34c759' }}>
            <Package size={24} />
          </div>
          <div className="mobile-hub-card-title">Receive Transfer</div>
          <div className="mobile-hub-card-subtitle">Inbound receipts</div>
        </button>

        <button className="mobile-hub-card" onClick={() => navigate('/mobile/warehouse/allocate')}>
          <div className="mobile-hub-card-icon" style={{ background: '#af52de' }}>
            <ClipboardList size={24} />
          </div>
          <div className="mobile-hub-card-title">Allocate</div>
          <div className="mobile-hub-card-subtitle">Assign to order</div>
        </button>

        <button className="mobile-hub-card" onClick={() => navigate('/mobile/warehouse/transfer')}>
          <div className="mobile-hub-card-icon" style={{ background: '#5ac8fa' }}>
            <ArrowRightLeft size={24} />
          </div>
          <div className="mobile-hub-card-title">Transfer</div>
          <div className="mobile-hub-card-subtitle">Move location</div>
        </button>

        <button className="mobile-hub-card" onClick={() => navigate('/mobile/warehouse/holds')}>
          <div className="mobile-hub-card-icon" style={{ background: '#ff3b30' }}>
            <AlertTriangle size={24} />
          </div>
          <div className="mobile-hub-card-title">Holds</div>
          <div className="mobile-hub-card-subtitle">{holdsCount} active</div>
        </button>

        <button className="mobile-hub-card" onClick={() => navigate('/mobile/warehouse/trucks')}>
          <div className="mobile-hub-card-icon" style={{ background: '#ff9500' }}>
            <Truck size={24} />
          </div>
          <div className="mobile-hub-card-title">Trucks</div>
          <div className="mobile-hub-card-subtitle">{trucksAtDock} at dock</div>
        </button>
      </div>
    </div>
  );
}
